import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  Search, X, ChevronDown, ChevronRight, Check, AlertTriangle,
  Loader2, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';

interface Permission {
  id: number;
  modulo: string;
  clave: string;
  nombre: string;
  descripcion: string;
}

interface PermissionGroup {
  modulo: string;
  permisos: Permission[];
}

interface Role {
  id: number;
  nombre: string;
  descripcion: string;
  estado: string;
  es_sistema: boolean;
  permisos: Permission[];
  usuarios_count: number;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

const API_BASE = APP_CONFIG.BACKEND_URL;

export default function RolesPage() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [roles, setRoles] = useState<Role[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formPermissions, setFormPermissions] = useState<Set<number>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    type: 'delete' | 'toggle';
    role: Role;
  } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        authFetch(`${API_BASE}/api/v1/roles`),
        authFetch(`${API_BASE}/api/v1/roles/permisos`),
      ]);
      if (rolesRes.ok) setRoles(await rolesRes.json());
      if (permsRes.ok) setPermissionGroups(await permsRes.json());
    } catch {
      addNotification({ type: 'error', title: 'Error', message: 'No se pudieron cargar los roles' });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredRoles = roles.filter(r =>
    r.nombre.toLowerCase().includes(search.toLowerCase()) ||
    r.descripcion?.toLowerCase().includes(search.toLowerCase())
  );

  function openCreate() {
    setEditingRole(null);
    setFormName('');
    setFormDesc('');
    setFormPermissions(new Set());
    setExpandedModules(new Set(permissionGroups.map(g => g.modulo)));
    setShowForm(true);
  }

  function openEdit(role: Role) {
    setEditingRole(role);
    setFormName(role.nombre);
    setFormDesc(role.descripcion || '');
    setFormPermissions(new Set(role.permisos.map(p => p.id)));
    setExpandedModules(new Set(permissionGroups.map(g => g.modulo)));
    setShowForm(true);
  }

  function toggleModule(modulo: string) {
    setExpandedModules(prev => {
      const next = new Set(prev);
      next.has(modulo) ? next.delete(modulo) : next.add(modulo);
      return next;
    });
  }

  function togglePermission(id: number) {
    setFormPermissions(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleModuleAll(group: PermissionGroup) {
    const allIds = group.permisos.map(p => p.id);
    const allChecked = allIds.every(id => formPermissions.has(id));
    setFormPermissions(prev => {
      const next = new Set(prev);
      allIds.forEach(id => allChecked ? next.delete(id) : next.add(id));
      return next;
    });
  }

  async function handleSave() {
    if (!formName.trim()) {
      addNotification({ type: 'warning', title: 'Validación', message: 'El nombre del rol es requerido' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        nombre: formName.trim(),
        descripcion: formDesc.trim(),
        permission_ids: Array.from(formPermissions),
        usuario_id: user?.id || '',
        usuario_nombre: user?.name || '',
      };

      const url = editingRole
        ? `${API_BASE}/api/v1/roles/${editingRole.id}`
        : `${API_BASE}/api/v1/roles`;

      const res = await authFetch(url, {
        method: editingRole ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al guardar');
      }

      addNotification({
        type: 'success', title: 'Éxito',
        message: editingRole ? 'Rol actualizado correctamente' : 'Rol creado correctamente',
        autoClose: true,
      });
      setShowForm(false);
      fetchData();
    } catch (e: any) {
      addNotification({ type: 'error', title: 'Error', message: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleEstado(role: Role) {
    const nuevoEstado = role.estado === 'activo' ? 'inactivo' : 'activo';
    try {
      const res = await authFetch(`${API_BASE}/api/v1/roles/${role.id}/estado`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estado: nuevoEstado,
          usuario_id: user?.id || '',
          usuario_nombre: user?.name || '',
        }),
      });
      if (!res.ok) throw new Error('Error al cambiar estado');
      addNotification({
        type: 'success', title: 'Éxito',
        message: `Rol ${nuevoEstado === 'activo' ? 'activado' : 'inactivado'} correctamente`,
        autoClose: true,
      });
      fetchData();
    } catch (e: any) {
      addNotification({ type: 'error', title: 'Error', message: e.message });
    }
    setConfirmDialog(null);
  }

  async function handleDelete(role: Role) {
    try {
      const res = await authFetch(
        `${API_BASE}/api/v1/roles/${role.id}?usuario_id=${encodeURIComponent(user?.id || '')}&usuario_nombre=${encodeURIComponent(user?.name || '')}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al eliminar');
      }
      addNotification({
        type: 'success', title: 'Éxito', message: 'Rol eliminado correctamente', autoClose: true,
      });
      fetchData();
    } catch (e: any) {
      addNotification({ type: 'error', title: 'Error', message: e.message });
    }
    setConfirmDialog(null);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Roles</h1>
          <p className="text-sm text-gray-500 mt-1">Administra los roles y permisos del sistema.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ backgroundColor: '#1B2A4A' }}
        >
          <Plus className="w-4 h-4" /> Nuevo Rol
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar roles..."
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent"
          style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
        />
      </div>

      {/* Roles list */}
      <div className="grid gap-4">
        {filteredRoles.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No se encontraron roles</p>
          </div>
        ) : (
          filteredRoles.map(role => (
            <div
              key={role.id}
              className="bg-white rounded-xl border border-gray-100 p-5 transition-all hover:shadow-md"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: role.estado === 'activo' ? '#EEF2FF' : '#F3F4F6' }}
                  >
                    <Shield
                      className="w-5 h-5"
                      style={{ color: role.estado === 'activo' ? '#4F46E5' : '#9CA3AF' }}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{role.nombre}</h3>
                      {role.es_sistema ? (
                        <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-600">
                          Sistema
                        </span>
                      ) : null}
                      <span
                        className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                          role.estado === 'activo' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
                        }`}
                      >
                        {role.estado === 'activo' ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{role.descripcion}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" /> {role.usuarios_count} usuario(s)
                      </span>
                      <span>{role.permisos.length} permiso(s)</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEdit(role)}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                    title="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDialog({ type: 'toggle', role })}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                    title={role.estado === 'activo' ? 'Inactivar' : 'Activar'}
                  >
                    {role.estado === 'activo' ? <ToggleRight className="w-4 h-4 text-green-500" /> : <ToggleLeft className="w-4 h-4" />}
                  </button>
                  {!role.es_sistema && (
                    <button
                      onClick={() => setConfirmDialog({ type: 'delete', role })}
                      className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Form Modal */}
      {createPortal(
      <AnimatePresence>
        {showForm && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[9998]"
              onClick={() => setShowForm(false)}
            />
            <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-[720px] max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingRole ? 'Editar Rol' : 'Nuevo Rol'}
                </h2>
                <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Nombre del rol *</label>
                  <input
                    value={formName}
                    onChange={e => setFormName(e.target.value)}
                    placeholder="Ej: Coordinador, Supervisor..."
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Descripción</label>
                  <textarea
                    value={formDesc}
                    onChange={e => setFormDesc(e.target.value)}
                    placeholder="Describe brevemente las responsabilidades de este rol..."
                    rows={2}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                    style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                  />
                </div>

                {/* Permissions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      Permisos ({formPermissions.size} seleccionados)
                    </label>
                    <button
                      onClick={() => {
                        const allIds = permissionGroups.flatMap(g => g.permisos.map(p => p.id));
                        const allChecked = allIds.every(id => formPermissions.has(id));
                        setFormPermissions(allChecked ? new Set() : new Set(allIds));
                      }}
                      className="text-xs font-medium hover:underline"
                      style={{ color: '#EDAA00' }}
                    >
                      {permissionGroups.flatMap(g => g.permisos).every(p => formPermissions.has(p.id))
                        ? 'Desmarcar todos'
                        : 'Seleccionar todos'}
                    </button>
                  </div>

                  <div className="space-y-2">
                    {permissionGroups.map(group => {
                      const isExpanded = expandedModules.has(group.modulo);
                      const checkedCount = group.permisos.filter(p => formPermissions.has(p.id)).length;
                      const allChecked = checkedCount === group.permisos.length;
                      const someChecked = checkedCount > 0 && !allChecked;

                      return (
                        <div key={group.modulo} className="border border-gray-100 rounded-xl overflow-hidden">
                          <div
                            className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => toggleModule(group.modulo)}
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="text-sm font-semibold text-gray-700">{group.modulo}</span>
                              <span className="text-xs text-gray-400">
                                {checkedCount}/{group.permisos.length}
                              </span>
                            </div>
                            <button
                              onClick={e => { e.stopPropagation(); toggleModuleAll(group); }}
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                allChecked
                                  ? 'border-transparent text-white'
                                  : someChecked
                                  ? 'border-gray-300'
                                  : 'border-gray-300'
                              }`}
                              style={allChecked ? { backgroundColor: '#EDAA00' } : someChecked ? { backgroundColor: '#FFF3D0' } : {}}
                            >
                              {allChecked && <Check className="w-3 h-3" />}
                              {someChecked && <div className="w-2 h-0.5 rounded" style={{ backgroundColor: '#EDAA00' }} />}
                            </button>
                          </div>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="px-4 py-2 space-y-1">
                                  {group.permisos.map(perm => (
                                    <label
                                      key={perm.id}
                                      className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                      <button
                                        onClick={() => togglePermission(perm.id)}
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                                          formPermissions.has(perm.id) ? 'border-transparent text-white' : 'border-gray-300'
                                        }`}
                                        style={formPermissions.has(perm.id) ? { backgroundColor: '#EDAA00' } : {}}
                                      >
                                        {formPermissions.has(perm.id) && <Check className="w-3 h-3" />}
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800">{perm.nombre}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{perm.descripcion}</p>
                                        <p className="text-[10px] text-gray-300 mt-0.5 font-mono">{perm.clave}</p>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#1B2A4A' }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingRole ? 'Guardar Cambios' : 'Crear Rol'}
                </button>
              </div>
            </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>,
      document.body)}

      {/* Confirm Dialog */}
      {createPortal(
      <AnimatePresence>
        {confirmDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[9998]"
              onClick={() => setConfirmDialog(null)}
            />
            <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-[420px] bg-white rounded-2xl shadow-2xl z-[9999] p-6 pointer-events-auto"
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  confirmDialog.type === 'delete' ? 'bg-red-100' : 'bg-amber-100'
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${
                    confirmDialog.type === 'delete' ? 'text-red-600' : 'text-amber-600'
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {confirmDialog.type === 'delete' ? 'Eliminar rol' : (
                      confirmDialog.role.estado === 'activo' ? 'Inactivar rol' : 'Activar rol'
                    )}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {confirmDialog.type === 'delete'
                      ? `¿Estás seguro de que deseas eliminar el rol "${confirmDialog.role.nombre}"? Esta acción no se puede deshacer.`
                      : `¿Deseas ${confirmDialog.role.estado === 'activo' ? 'inactivar' : 'activar'} el rol "${confirmDialog.role.nombre}"?`
                    }
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => confirmDialog.type === 'delete'
                    ? handleDelete(confirmDialog.role)
                    : handleToggleEstado(confirmDialog.role)
                  }
                  className={`px-4 py-2 text-sm font-semibold text-white rounded-xl transition-all hover:opacity-90 ${
                    confirmDialog.type === 'delete' ? 'bg-red-600' : ''
                  }`}
                  style={confirmDialog.type !== 'delete' ? { backgroundColor: '#1B2A4A' } : {}}
                >
                  {confirmDialog.type === 'delete' ? 'Eliminar' : 'Confirmar'}
                </button>
              </div>
            </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>,
      document.body)}
    </div>
  );
}
